import gravatarUrl from "gravatar-url";
import moment from "moment";
import React, { useEffect, useState } from "react";
import {
    membersApi,
    useAddMemberMutation,
    useDeleteMemberMutation,
    useGetMembersQuery,
} from "../../features/members/membersApi";
import Modal from "../ui/Modal";
import deleteIcon from "../../assets/images/delete.png";
import capitalizeFirstLetter from "../../utils/capitalizeString";
import { useDeleteTeamMutation } from "../../features/teams/teamsApi";
import { useGetAllUsersQuery } from "../../features/users/usersApi";
import debounceHandler from "../../utils/debounceHandler";
import { useDispatch, useSelector } from "react-redux";

export default function Team({ team }) {
    const { name, description, color, timestamp, id } = team || {};

    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth) || {};
    const { email } = user || {};

    const { data: allUsers } = useGetAllUsersQuery();

    const { data: teamMembers } = useGetMembersQuery(id) || {};

    const [deleteTeam, {}] = useDeleteTeamMutation();
    const [deleteMember, {}] = useDeleteMemberMutation();
    const [addMember, { isSuccess: isAddMemberSuccess }] =
        useAddMemberMutation();

    const [memberModal, setMemberModal] = useState(false);
    const [memberAddModal, setMemberAddModal] = useState(false);
    const [userEmail, setUserEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    const style = { background: color ? color : "#0cd814" };

    const memberModalOpen = () => setMemberModal(true);
    const memberModalClose = () => setMemberModal(false);

    const memberAddModalOpen = () => setMemberAddModal(true);
    const memberAddModalClose = () => setMemberAddModal(false);

    const handleDelete = (e) => {
        e.preventDefault();
        deleteTeam(id);
    };

    const doSearch = (value) => {
        setEmailError("");
        // check team name in database
        setUserEmail(value);

        dispatch(
            membersApi.endpoints.getMemberInTeam.initiate({ email: value, id })
        )
            .unwrap()
            .then((data) => {
                if (data.length > 0) {
                    setEmailError("Team member is already exists");
                }
            })
            .catch((err) => {});
    };

    const handleSearch = debounceHandler(doSearch, 500);

    useEffect(() => {
        memberAddModalClose();
        setUserEmail("");
    }, [isAddMemberSuccess]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setEmailError("");

        if (!userEmail) {
            setEmailError("Member email is required!!");
            return;
        }

        if (!emailError && userEmail) {
            const selectMember = allUsers.find((u) => u.email === userEmail);

            const data = {
                email: selectMember.email,
                name: selectMember.name,
                isCreator: false,
                teamId: id,
            };
            addMember(data);
        }
    };

    const handleMemberDelete = (id) => {
        deleteMember(id);
    };

    return (
        <>
            <div
                className="relative flex flex-col items-start p-4 mt-3 bg-white rounded-lg cursor-pointer bg-opacity-90 group hover:bg-opacity-100"
                draggable="true"
            >
                <span className="absolute top-0 right-0 float-right flex items-center justify-center ml-auto">
                    <img
                        onClick={handleDelete}
                        src={deleteIcon}
                        alt=""
                        className="w-4 h-4 mt-3 flex hidden items-center justify-center group-hover:flex"
                    />
                    <button
                        onClick={memberAddModalOpen}
                        className="flex items-center justify-center hidden w-5 h-5 mt-3 mr-2 text-gray-500 rounded  hover:text-gray-700 group-hover:flex"
                    >
                        <svg
                            className="w-4 h-4 fill-current"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                    </button>
                </span>
                <span
                    className="flex items-center h-6 px-3 text-xs font-semibold text-white rounded-full"
                    style={style}
                >
                    {capitalizeFirstLetter(name)}
                </span>
                <h4 className="mt-3 text-sm font-medium">{description}</h4>
                <div className="flex items-center w-full mt-3 text-xs font-medium text-gray-400">
                    <div className="flex items-center w-full">
                        <svg
                            className="w-4 h-4 text-gray-300 fill-current"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <span className="ml-1 leading-none">
                            {moment(timestamp).format("MMM d")}
                        </span>
                        <span
                            className="ml-auto flex"
                            onClick={memberModalOpen}
                        >
                            <span className="text-green-500 mr-2 flex justify-center items-center">
                                Members
                            </span>
                            <span className="flex">
                                {teamMembers
                                    ?.slice(0, 3)
                                    ?.map((member, index) => (
                                        <img
                                            className="w-6 h-6 rounded-full"
                                            key={member.email}
                                            src={gravatarUrl(member.email, {
                                                size: 80,
                                            })}
                                            alt={member.name}
                                        />
                                    ))}
                                {teamMembers?.length > 3 && (
                                    <span className="w-6 h-6 rounded-full text-black bg-slate-300 flex justify-center items-center">
                                        {teamMembers.length - 3}+
                                    </span>
                                )}
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            {memberModal && (
                <Modal
                    open={memberModal}
                    close={memberModalClose}
                    title="Team members"
                >
                    {teamMembers?.map((m) => (
                        <span key={m.id} className="flex items-center my-2">
                            <img
                                className="w-6 h-6 rounded-full"
                                src={gravatarUrl(m.email, {
                                    size: 80,
                                })}
                                alt={m.name}
                            />
                            <span className="text-black ml-2">{m.name}</span>
                            {!m.isCreator && (
                                <img
                                    onClick={() => handleMemberDelete(m.id)}
                                    src={deleteIcon}
                                    alt=""
                                    className="w-5 h-5 ml-auto cursor-pointer"
                                />
                            )}
                        </span>
                    ))}
                    {teamMembers?.length === 0 && (
                        <span className="text-yellow-500">
                            There is no team member
                        </span>
                    )}
                </Modal>
            )}

            {memberAddModal && (
                <Modal
                    open={memberAddModal}
                    close={memberAddModalClose}
                    title="Add team member"
                >
                    <form className="space-y-3" onSubmit={handleSubmit}>
                        <div>
                            <label
                                htmlFor="userEmail"
                                className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-300"
                            >
                                Select member
                            </label>
                            <select
                                onChange={(e) => handleSearch(e.target.value)}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-200 focus:border-blue-300 block w-full p-1.5"
                            >
                                <option value="">Select an user</option>
                                {allUsers.map((u) => (
                                    <option key={u.id} value={u.email}>
                                        {u.email}
                                    </option>
                                ))}
                            </select>
                            {emailError && (
                                <span className="text-red-600">
                                    {emailError}
                                </span>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={emailError}
                            className="w-full text-white bg-slate-700 hover:bg-slate-800 focus:ring-2 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center "
                        >
                            Save
                        </button>
                    </form>
                </Modal>
            )}
        </>
    );
}
